import React, { useContext, useState, useEffect } from 'react';
import { UserProfileContext } from '../../../context/userProfileContext';
import { Mortgage } from '../../../interfaces/Mortgage';
import { useEnumsContext } from '../../../context/EnumsContext';
import Select from 'react-select';
import { Form, Button, Card, Table, Row, Col } from 'react-bootstrap';
import { APICore } from '../../../utils/api/apiCore';
import { getFiles } from '../../../utils/api/files';
import { sendBankEmails, getBankEmails } from '../../../utils/api/mortgage';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { ClientFile } from '../../../interfaces/ClientFile';
import { formatDate } from '../../../utils/formatDate';

interface BankEmail {
    _id: string;
    bank: { name: string; number: number };
    subject: string;
    content: string;
    clientId: string;
    mortgageId: string;
    attachments: string[];
    createdAt: string;
}

const BankEmails = () => {
    const enums = useEnumsContext();
    const { profileContextData } = useContext(UserProfileContext);
    const { client } = profileContextData;
    const mortgage = profileContextData.currentRound as Mortgage;

    const [selectedBanks, setSelectedBanks] = useState<any[]>([]);
    const [emailContent, setEmailContent] = useState('');
    const [mortgageFiles, setMortgageFiles] = useState<ClientFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [bankEmails, setBankEmails] = useState<BankEmail[]>([]);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);

    // Generate email subject from client data
    const emailSubject = client ? `${client.firstName} ${client.lastName} - ${client.idNumber || ''}` : '';

    useEffect(() => {
        // Fetch mortgage files using same approach as Files tab
        const fetchFiles = async () => {
            setIsLoading(true);
            try {
                const query: { ids?: string; clientId: string } = {
                    clientId: client._id,
                };

                if (mortgage) {
                    if (mortgage.files && mortgage.files.length) {
                        query.ids = mortgage.files.join(',');
                    } else {
                        setMortgageFiles([]);
                        setIsLoading(false);
                        return;
                    }
                }

                const results = await getFiles(query);
                setMortgageFiles(results.files);
            } catch (error) {
                console.error('Error fetching mortgage files:', error);
                globalErrorHandler(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (client?._id) {
            fetchFiles();
        }
    }, [client, mortgage]);

    useEffect(() => {
        // Fetch bank emails history
        const fetchBankEmails = async () => {
            if (!client?._id) return;

            setIsLoadingEmails(true);
            try {
                const response = await getBankEmails(client._id);
                setBankEmails(response.emails);
            } catch (error) {
                console.error('Error fetching bank emails:', error);
            } finally {
                setIsLoadingEmails(false);
            }
        };

        fetchBankEmails();
    }, [client]);

    const getBankOptions = () => {
        if (!enums?.Banks) return [];
        return enums.Banks.map((bank: any) => ({
            label: `${bank.name} (${bank.number})`,
            value: bank.number,
        }));
    };

    const handleBankSelection = (selected: any) => {
        setSelectedBanks(selected || []);
    };

    const handleFileSelection = (fileId: string) => {
        setSelectedFiles((prevSelected) => {
            if (prevSelected.includes(fileId)) {
                return prevSelected.filter((id) => id !== fileId);
            } else {
                return [...prevSelected, fileId];
            }
        });
    };

    const handleSendEmail = async () => {
        if (!selectedBanks.length || !emailContent.trim()) {
            return;
        }

        setIsSending(true);
        try {
            const payload = {
                banks: selectedBanks.map((bank) => bank.value),
                subject: emailSubject,
                content: emailContent,
                attachmentFileIds: selectedFiles,
                clientId: client._id,
                mortgageId: mortgage._id,
            };

            await sendBankEmails(payload);
            alert('Email sent successfully!');

            // Refresh bank emails list
            const response = await getBankEmails(client._id);
            setBankEmails(response.emails);

            // Reset form fields after successful send
            setEmailContent('');
            setSelectedFiles([]);
        } catch (error) {
            console.error('Error sending email to banks:', error);
            globalErrorHandler(error);
            alert('Failed to send email. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const getBankName = (bankNumber: number) => {
        const bank = enums?.Banks?.find((b: any) => b.number === bankNumber);
        return bank ? bank.name : bankNumber;
    };

    return (
        <div className="detailed-tab">
            {client && mortgage ? (
                <Row>
                    <Col md={6}>
                        <h4 className="mb-3">Compose Bank Email</h4>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Banks</Form.Label>
                                <Select
                                    isMulti
                                    options={getBankOptions()}
                                    onChange={handleBankSelection}
                                    value={selectedBanks}
                                    placeholder="Select banks..."
                                    className="bank-select"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Subject</Form.Label>
                                <Form.Control type="text" value={emailSubject} readOnly />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Content</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={emailContent}
                                    onChange={(e) => setEmailContent(e.target.value)}
                                    placeholder="Type your email content here..."
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Attach Files</Form.Label>
                                {isLoading ? (
                                    <p>Loading files...</p>
                                ) : mortgageFiles.length > 0 ? (
                                    <div className="file-list">
                                        {mortgageFiles.map((file) => (
                                            <Form.Check
                                                key={file._id}
                                                type="checkbox"
                                                id={`file-${file._id}`}
                                                label={file.fileName}
                                                checked={selectedFiles.includes(file._id)}
                                                onChange={() => handleFileSelection(file._id)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <p>No files available</p>
                                )}
                            </Form.Group>

                            <Button variant="primary" onClick={handleSendEmail} disabled={!selectedBanks.length || !emailContent.trim() || isSending}>
                                {isSending ? 'Sending...' : 'Send Email'}
                            </Button>
                        </Form>
                    </Col>

                    <Col md={6}>
                        <h4 className="mb-3">Email History</h4>
                        {isLoadingEmails ? (
                            <p>Loading email history...</p>
                        ) : (
                            <>
                                {bankEmails && bankEmails.length > 0 ? (
                                    <Card>
                                        <Card.Body>
                                            <Table responsive className="table-centered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Bank</th>
                                                        <th>Subject</th>
                                                        <th>Content</th>
                                                        <th>Attachments</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bankEmails.map((email) => (
                                                        <tr key={email._id}>
                                                            <td>{email.createdAt ? formatDate(new Date(email.createdAt)) : 'N/A'}</td>
                                                            <td>{email.bank.name}</td>
                                                            <td>{email.subject || 'N/A'}</td>
                                                            <td>
                                                                {email.content
                                                                    ? email.content.length > 50
                                                                        ? `${email.content.substring(0, 50)}...`
                                                                        : email.content
                                                                    : 'N/A'}
                                                            </td>
                                                            <td>{email.attachments?.length || 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                ) : (
                                    <p>No emails have been sent to banks yet.</p>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
            ) : (
                <div>No client or mortgage data available.</div>
            )}
        </div>
    );
};

export default BankEmails;
